import React, { useState, useRef, useEffect, useCallback } from 'react';
import api from '../api/axios';
import { C } from '../theme';

const COLLAPSED = 52;                 // handle bar height (px)
const EXPANDED  = () => Math.round(window.innerHeight * 0.72);

const SUGGESTIONS = [
  'status of serial …', 'client CLT-0003', 'stock summary', 'dealer credits',
];

const STATUS_COLOR = {
  'In Stock': '#15803D', installed: '#065F46', Installed: '#065F46',
  dispatched: '#1D4ED8', Dispatched: '#1D4ED8', cancelled: '#991B1B',
  Cancelled: '#991B1B', damaged: '#92400E', replacement: '#0369A1',
};

const WELCOME = {
  title: "👋 HIVE Assistant",
  summary: "Ask me about a device, client, inventory stock, or dealer wallet — I answer straight from your database.",
  sections: [{ kind: 'kv', heading: 'Try asking', rows: [
    ['Device status', 'status of serial 8991… / INV-0007 / ref 9360399813'],
    ['Client details', 'client CLT-0003 · client 9360399813'],
    ['Inventory', 'stock summary · how many Sim in stock'],
    ['Dealer credits', 'dealer credits · wallet balance of Skynav'],
  ] }],
};

export default function AssistantDock() {
  const [height, setHeight]   = useState(COLLAPSED);   // live height while dragging / settled
  const [open,   setOpen]     = useState(false);
  const [messages, setMessages] = useState([{ role: 'bot', data: WELCOME }]);
  const [input,  setInput]    = useState('');
  const [loading, setLoading] = useState(false);

  const drag = useRef(null);      // { startY, startH }
  const scrollRef = useRef(null);

  const snap = useCallback((toOpen) => {
    setOpen(toOpen);
    setHeight(toOpen ? EXPANDED() : COLLAPSED);
  }, []);

  useEffect(() => {
    if (open) scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, loading, open]);

  // ── Drag handling (pointer + touch) ──────────────────────────────────────
  const onDown = (e) => {
    const y = e.touches ? e.touches[0].clientY : e.clientY;
    drag.current = { startY: y, startH: height, moved: false };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onUp);
  };
  const onMove = (e) => {
    if (!drag.current) return;
    const y = e.touches ? e.touches[0].clientY : e.clientY;
    const dy = drag.current.startY - y;                 // up = positive
    if (Math.abs(dy) > 4) drag.current.moved = true;
    if (e.cancelable) e.preventDefault();
    const max = EXPANDED();
    const next = Math.max(COLLAPSED, Math.min(max, drag.current.startH + dy));
    setHeight(next);
  };
  const onUp = () => {
    window.removeEventListener('mousemove', onMove);
    window.removeEventListener('mouseup', onUp);
    window.removeEventListener('touchmove', onMove);
    window.removeEventListener('touchend', onUp);
    if (!drag.current) return;
    const { startH, moved } = drag.current;
    drag.current = null;
    if (!moved) { snap(!open); return; }                // tap = toggle
    const max = EXPANDED();
    // settle to whichever snap point is nearer, biased to intent of the drag
    setHeight(h => {
      const shouldOpen = h > (COLLAPSED + max) / 2.6;
      setOpen(shouldOpen);
      return shouldOpen ? max : COLLAPSED;
    });
    void startH;
  };

  // keep expanded height correct on resize
  useEffect(() => {
    const onResize = () => { if (open) setHeight(EXPANDED()); };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [open]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || loading) return;
    if (!open) snap(true);
    setMessages(m => [...m, { role: 'user', text: q }]);
    setInput('');
    setLoading(true);
    try {
      const r = await api.post('/api/admin/assistant', { query: q });
      setMessages(m => [...m, { role: 'bot', data: r.data }]);
    } catch (e) {
      setMessages(m => [...m, { role: 'bot', data: {
        title: 'Something went wrong',
        summary: e?.response?.data?.detail || 'Could not reach the assistant.',
        sections: [],
      } }]);
    } finally { setLoading(false); }
  };

  const dragging = !!drag.current;

  return (
    <div style={{
      position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 900,
      height: `${height}px`,
      background: C.white, borderTop: `1px solid ${C.border}`,
      boxShadow: '0 -8px 30px rgba(0,0,0,0.12)',
      borderRadius: open ? '16px 16px 0 0' : '14px 14px 0 0',
      display: 'flex', flexDirection: 'column',
      transition: dragging ? 'none' : 'height 0.26s cubic-bezier(0.32,0.72,0,1)',
      overflow: 'hidden',
    }}>

      {/* ── Drag handle / bar ── */}
      <div
        onMouseDown={onDown}
        onTouchStart={onDown}
        style={{
          height: `${COLLAPSED}px`, flexShrink: 0, cursor: 'grab', userSelect: 'none',
          display: 'flex', alignItems: 'center', gap: '12px', padding: '0 20px',
          background: open ? '#FCFCFD' : `linear-gradient(90deg, ${C.primaryBg}, ${C.white})`,
          borderBottom: open ? `1px solid ${C.border}` : 'none',
        }}
      >
        {/* grab pill */}
        <div style={{ position: 'absolute', left: '50%', top: '7px', transform: 'translateX(-50%)', width: '40px', height: '4px', borderRadius: '4px', background: '#D1D5DB' }} />
        <div style={{ width: '30px', height: '30px', borderRadius: '9px', background: 'linear-gradient(135deg,#6366F1,#4338CA)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', flexShrink: 0 }}>🤖</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '13px', fontWeight: 800, color: C.textMain }}>HIVE Assistant</div>
          <div style={{ fontSize: '11px', color: C.textSub }}>
            {open ? 'Drag down to minimise' : 'Drag up or tap to chat — ask about devices, clients, stock, credits'}
          </div>
        </div>
        <span style={{ fontSize: '15px', color: C.textSub, transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'none' }}>⌃</span>
      </div>

      {/* ── Chat (only meaningful when expanded) ── */}
      {height > COLLAPSED + 8 && (
        <>
          <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: '14px 18px', background: '#FAFAFB' }}>
            <div style={{ maxWidth: '840px', margin: '0 auto' }}>
              {messages.map((m, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start', marginBottom: '14px' }}>
                  {m.role === 'user' ? (
                    <div style={{ background: `linear-gradient(135deg, ${C.primary}, ${C.primaryDark})`, color: '#fff', padding: '9px 15px', borderRadius: '15px 15px 4px 15px', fontSize: '13px', fontWeight: 600, maxWidth: '78%' }}>{m.text}</div>
                  ) : (
                    <div style={{ display: 'flex', gap: '9px', maxWidth: '94%' }}>
                      <div style={{ width: '28px', height: '28px', borderRadius: '8px', flexShrink: 0, background: '#EEF2FF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '15px' }}>🤖</div>
                      <BotCard data={m.data} onChip={send} />
                    </div>
                  )}
                </div>
              ))}
              {loading && (
                <div style={{ display: 'flex', gap: '9px' }}>
                  <div style={{ width: '28px', height: '28px', borderRadius: '8px', background: '#EEF2FF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '15px' }}>🤖</div>
                  <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: '4px 14px 14px 14px', padding: '11px 16px', color: C.textSub, fontSize: '13px' }}>Looking that up…</div>
                </div>
              )}
            </div>
          </div>

          {/* Composer */}
          <div style={{ flexShrink: 0, borderTop: `1px solid ${C.border}`, background: C.white, padding: '10px 18px' }}>
            <div style={{ maxWidth: '840px', margin: '0 auto' }}>
              <div style={{ display: 'flex', gap: '7px', flexWrap: 'wrap', marginBottom: '8px' }}>
                {SUGGESTIONS.map(s => (
                  <button key={s} onClick={() => setInput(s)} style={{ padding: '4px 11px', borderRadius: '20px', border: `1px solid ${C.border}`, background: C.white, color: C.textSub, fontSize: '11px', fontWeight: 600, cursor: 'pointer' }}>{s}</button>
                ))}
              </div>
              <div style={{ display: 'flex', gap: '9px' }}>
                <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') send(); }}
                  placeholder="Ask about a device, client, stock, or dealer credit…"
                  style={{ flex: 1, padding: '11px 16px', border: `1.5px solid ${C.border}`, borderRadius: '11px', fontSize: '13px', outline: 'none' }} />
                <button onClick={() => send()} disabled={loading || !input.trim()} style={{
                  padding: '0 20px', borderRadius: '11px', border: 'none',
                  background: (loading || !input.trim()) ? '#D1D5DB' : `linear-gradient(135deg, ${C.primary}, ${C.primaryDark})`,
                  color: '#fff', fontSize: '15px', fontWeight: 800, cursor: (loading || !input.trim()) ? 'default' : 'pointer',
                }}>➤</button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* ── Rich answer rendering ─────────────────────────────────────────────────── */

function BotCard({ data, onChip }) {
  if (!data) return null;
  const intentColor = {
    device: '#0369A1', client: '#5B21B6', inventory: '#15803D',
    dealer_credits: '#B45309', not_found: '#991B1B', help: C.primaryDeep,
  }[data.intent] || C.textMain;
  return (
    <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: '4px 14px 14px 14px', overflow: 'hidden', width: '100%' }}>
      <div style={{ padding: '11px 16px', borderBottom: `1px solid ${C.border}`, background: '#FCFCFD' }}>
        {data.title && <div style={{ fontSize: '13px', fontWeight: 800, color: intentColor }}>{data.title}</div>}
        {data.summary && <div style={{ fontSize: '12.5px', color: C.textMain, marginTop: '3px', lineHeight: 1.5 }} dangerouslySetInnerHTML={{ __html: mdBold(data.summary) }} />}
      </div>
      <div style={{ padding: '13px 16px', display: 'flex', flexDirection: 'column', gap: '15px' }}>
        {(data.sections || []).map((s, i) => <Section key={i} s={s} onChip={onChip} />)}
      </div>
    </div>
  );
}

function Section({ s, onChip }) {
  if (s.kind === 'kv') {
    return (
      <div>
        {s.heading && <SecHead>{s.heading}</SecHead>}
        <table style={{ width: '100%', borderCollapse: 'collapse' }}><tbody>
          {s.rows.map((r, i) => (
            <tr key={i} style={{ borderBottom: i < s.rows.length - 1 ? `1px solid ${C.primaryBg}` : 'none' }}>
              <td style={{ padding: '5px 0', fontSize: '12px', color: C.textSub, fontWeight: 600, width: '38%', verticalAlign: 'top' }}>{r[0]}</td>
              <td style={{ padding: '5px 0', fontSize: '12.5px', color: C.textMain, fontWeight: 600 }}>
                {s.heading === 'Try asking'
                  ? <button onClick={() => onChip(r[1].split('·')[0].trim())} style={{ background: 'none', border: 'none', padding: 0, color: C.primaryDeep, fontSize: '12.5px', fontWeight: 600, cursor: 'pointer', textAlign: 'left' }}>{r[1]}</button>
                  : r[1]}
              </td>
            </tr>
          ))}
        </tbody></table>
      </div>
    );
  }
  if (s.kind === 'stats') {
    return (
      <div>
        {s.heading && <SecHead>{s.heading}</SecHead>}
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {s.items.map((it, i) => (
            <div key={i} style={{ background: '#F8FAFC', border: `1px solid ${C.border}`, borderRadius: '9px', padding: '8px 13px', minWidth: '80px' }}>
              <div style={{ fontSize: '17px', fontWeight: 900, color: C.textMain }}>{it.value}</div>
              <div style={{ fontSize: '10px', fontWeight: 700, color: C.textSub, textTransform: 'capitalize' }}>{it.label}</div>
            </div>
          ))}
        </div>
      </div>
    );
  }
  if (s.kind === 'timeline') {
    return (
      <div>
        {s.heading && <SecHead>{s.heading}</SecHead>}
        {s.events.length === 0 ? <div style={{ fontSize: '12px', color: C.textSub }}>No recorded events.</div>
          : s.events.map((e, i) => (
            <div key={i} style={{ display: 'flex', gap: '9px', paddingBottom: i < s.events.length - 1 ? '11px' : 0 }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                <div style={{ width: '24px', height: '24px', borderRadius: '50%', background: C.primaryBg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px' }}>{e.icon}</div>
                {i < s.events.length - 1 && <div style={{ width: '2px', flex: 1, background: C.border, marginTop: '2px' }} />}
              </div>
              <div style={{ paddingBottom: '2px' }}>
                <div style={{ fontSize: '12.5px', fontWeight: 700, color: C.textMain }}>{e.title}</div>
                {e.detail && <div style={{ fontSize: '11.5px', color: C.textSub }}>{e.detail}</div>}
                <div style={{ fontSize: '10px', color: '#9CA3AF', marginTop: '1px' }}>{e.date}</div>
              </div>
            </div>
          ))}
      </div>
    );
  }
  if (s.kind === 'table') {
    return (
      <div>
        {s.heading && <SecHead>{s.heading}</SecHead>}
        <div style={{ overflowX: 'auto', border: `1px solid ${C.border}`, borderRadius: '9px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '340px' }}>
            <thead><tr style={{ background: C.primaryBg }}>
              {s.columns.map(h => <th key={h} style={{ padding: '7px 11px', fontSize: '10px', fontWeight: 800, color: C.textSub, textAlign: 'left', textTransform: 'uppercase', letterSpacing: '0.4px', whiteSpace: 'nowrap' }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {s.rows.map((r, i) => (
                <tr key={i} style={{ borderTop: `1px solid ${C.primaryBg}`, background: i % 2 ? '#FAFAFA' : C.white }}>
                  {r.map((cell, j) => {
                    const col = STATUS_COLOR[cell];
                    return <td key={j} style={{ padding: '7px 11px', fontSize: '12px', color: col || C.textMain, fontWeight: col ? 700 : 500, whiteSpace: j === r.length - 1 ? 'normal' : 'nowrap' }}>{cell}</td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }
  return null;
}

function SecHead({ children }) {
  return <div style={{ fontSize: '11px', fontWeight: 800, color: C.textSub, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '7px' }}>{children}</div>;
}

function mdBold(s) {
  return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
}
